import _ from 'lodash'
import { JSONEditor } from '@json-editor/json-editor'
import Choices from 'choices.js'
import Mustache from 'mustache'
import EventBus from '../../helpers/event-bus'
import gsap  from 'gsap'
import Draggable from 'gsap/Draggable'

gsap.registerPlugin(Draggable);
const ArrayEditor = JSONEditor.defaults.editors.array

class LargeArrayEditor extends JSONEditor.AbstractEditor {
  constructor (options, defaults) {
    super(options, defaults)

    this.isBasic = options.jsoneditor.options.isBasic || false
    this.schema.minItems = this.schema.minItems || 0
    this.schema.maxItems = this.schema.maxItems || Infinity
    this.options.disable_array_reorder = false
    this.options.disable_array_add = false
    this.options.disable_array_delete = false
    if (this.isBasic) {
      // Set default values
      this.options.disable_array_reorder = true
      this.options.disable_array_add = true
      this.options.disable_array_delete = true
      if (this.options.basicEditorOptions) {
        _.merge(this, this.options.basicEditorOptions)
      }
    }
    this.refreshValue = _.debounce((force) => {
      if (this.useTabs) {
        this._tabsRefreshValue()
      } else {
        this.updateChoices()
        this.serialized = JSON.stringify(this.value)
      }
    }, 1)

    this.onChildEditorChange = _.debounce((editor) => {
      if (this.ready && this.currentIndex >= 0) {
        try {
          this.value[this.currentIndex] = editor.getValue()
          this.refreshValue()
          if (this.useTabs) {
            this._refreshTabLabels()
          }
          super.onChildEditorChange(editor)
        } catch (err) {
          console.error(err)
        }
      }
    }, 1)
  }

  register () {
    super.register()
    this.child.register()
  }

  unregister () {
    super.unregister()
    this.child.unregister()
  }

  preBuild () {
    this.ready = false
    this.child = null
    this.currentIndex = undefined
    this.useTabs = this.schema?.options?.tabbed || false
    this.rows = []
    this.row_cache = []
    this.active_tab = null
  }

  // ─── BUILD ──────────────────────────────────────────────────────────────────

  build () {
    const template = this.schema.items.headerTemplate
    this.itemTitleTmpl = (data) => Mustache.render(template, data)

    if (this.useTabs) {
      this._buildTabs()
    } else {
      this._buildDefault()
    }
  }

  /** Tabs mode: left pill-nav (labels only) + right single shared child editor */
  _buildTabs () {
    this.container.classList.add('je-array__tabs', 'col-md-12')

    // Title / header
    this.header = document.createElement('span')
    this.header.textContent = this.getTitle()
    this.title = this.theme.getHeader(this.header)
    this.container.appendChild(this.title)

    if (this.schema.description) {
      this.description = this.theme.getDescription(this.schema.description)
      this.container.appendChild(this.description)
    }

    // Controls (add / delete-last / delete-all) go into the title
    this.controls = this.theme.getHeaderButtonHolder()
    this.title.appendChild(this.controls)


    this.add_row_button = this._tabsCreateAddButton()

    this.tabs_holder = this.theme.getTabHolder(this.getValidId(this.getItemTitle()))
    const dragDirections = document.createElement('p')
    dragDirections.classList.add('description-text', 'form-text')
    dragDirections.textContent = 'Click a content block to view its details, or drag to reorder.'
    this.container.appendChild(dragDirections)
    this.container.appendChild(this.tabs_holder)

    this.row_holder = this.theme.getTabContentHolder(this.tabs_holder)

    this.holder = this.theme.getTabContent()
    this.holder.classList.add('active')
    this.row_holder.appendChild(this.holder)

    const editorClass = this.jsoneditor.getEditorClass(this.schema.items)
    this.child = this.jsoneditor.createEditor(editorClass, {
      jsoneditor: this.jsoneditor,
      schema: this.schema.items,
      container: this.holder,
      path: this.path + '.child',
      parent: this,
      required: true,
      disable_properties: true
    })

    this.child.preBuild()
    this.child.build()
    this.child.postBuild()
    // buttons
    this.moveUpButton = this.getButton('', (this.schema.format === 'tabs-top' ? 'moveleft' : 'moveup'), 'button_move_up_title')
    this.moveUpButton.classList.add('moveup', 'json-editor-btntype-move')
    this.moveUpButton.setAttribute('data-i', this.child.key)
    this.moveUpButton.addEventListener('click', e => {
      e.preventDefault()
      e.stopPropagation()
      this.moveUp(this.child.key, e)
    })

    this.moveDownButton = this.getButton('', (this.schema.format === 'tabs-top' ? 'moveright' : 'movedown'), 'button_move_down_title')
    this.moveDownButton.classList.add('movedown', 'json-editor-btntype-move')
    this.moveDownButton.setAttribute('data-i', this.child.key)
    this.moveDownButton.addEventListener('click', e => {
      e.preventDefault()
      e.stopPropagation()
      this.moveDown(this.child.key, e)
    })

    this.deleteButton = this.getButton('', 'delete', this.translate('button_delete_row_title', [this.getItemTitle()]))
    this.deleteButton.classList.add('delete', 'json-editor-btntype-delete')
    this.deleteButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.deleteChild()
    })

    this.copyButton = this.getButton('', 'copy', this.translate('button_copy_row_title', ['Item']))
    this.copyButton.classList.remove('btn-outline-secondary')
    this.copyButton.classList.add('btn-secondary', 'copy')
    this.copyButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.duplicateChild()
    })

    if (this.child.title) {
      this.child.title.appendChild(this.moveUpButton)
      this.child.title.appendChild(this.moveDownButton)
      this.child.title.appendChild(this.copyButton)
      this.child.title.appendChild(this.deleteButton)
    }

    this.holder.style.display = 'none'
  }

  /** Default (non-tabs) mode: Choices dropdown + single shared child editor */
  _buildDefault () {
    this.container.classList.add('large-array')

    this.header = document.createElement('span')
    this.header.textContent = this.getTitle()
    this.title = this.theme.getHeader(this.header)
    this.container.appendChild(this.title)
    this.titleControls = this.theme.getHeaderButtonHolder()
    this.title.appendChild(this.titleControls)

    if (this.schema.description) {
      this.description = this.theme.getDescription(this.schema.description)
      this.container.appendChild(this.description)
    }

    this.controls = this.theme.getHeaderButtonHolder()
    this.title.appendChild(this.controls)
    this.controls.classList.add('large-array-controls')

    // Dropdown selector
    this.input = this.theme.getSelectInput()
    this.input.addEventListener('choice', ({ detail }) => {
      const nextIndex = Number.parseInt(detail.choice.value, 10)
      this.selectChild(Number.isNaN(nextIndex) ? detail.choice.value : nextIndex)
    })

    // Child editor holder
    this.holder = this.theme.getChildEditorHolder()
    this.holder.classList.add('large-array-child', 'card', 'card-body', 'bg-white')
    this.holder.style.display = 'none'

    // Delete button
    this.deleteButton = this.getButton('', 'delete', this.translate('button_delete_row_title', ['Item']))
    this.deleteButton.classList.add('delete')
    this.deleteButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.deleteChild()
    })

    const buttonLabel = _.get(this.options, 'buttonLabel', this.getItemTitle())

    // Add button
    this.addButton = this.getButton(buttonLabel, 'add', this.translate('button_add_row_title', ['Item']))
    this.addButton.classList.add('add')
    this.addButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.addChild()
    })

    // Copy button
    this.copyButton = this.getButton('', 'copy', this.translate('button_copy_row_title', ['Item']))
    this.copyButton.classList.remove('btn-outline-secondary')
    this.copyButton.classList.add('btn-secondary', 'copy')
    this.copyButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.duplicateChild()
    })

    this.titleControls.appendChild(this.input)
    this.titleControls.appendChild(this.addButton)
    this.controls.appendChild(this.copyButton)
    this.controls.appendChild(this.deleteButton)

    // Build the single shared child editor
    const editorClass = this.jsoneditor.getEditorClass(this.schema.items)
    this.child = this.jsoneditor.createEditor(editorClass, {
      jsoneditor: this.jsoneditor,
      schema: this.schema.items,
      container: this.holder,
      path: this.path + '.child',
      parent: this,
      required: true,
      disable_properties: true
    })

    this.container.appendChild(this.holder)
    this.child.preBuild()
    this.child.build()
    this.child.postBuild()

    if (this.child.title) {
      this.child.title.insertBefore(this.controls, this.child.title.childNodes[this.child.title.childNodes.length - 1])
    }

    this.choices = new Choices(this.input, {
      itemSelectText: '',
      searchFields: ['label'],
      searchResultLimit: 100
    })
  }

  postBuild () {
    super.postBuild()
    this.ready = true
  }

  // ─── TABS MODE – NAV ENTRIES (lightweight, nav labels only) ────────────────

  /**
   * Add a nav pill entry for a single item. No editor is created here — values
   * are only loaded into the shared child editor when the tab is clicked.
   */
  _addTabEntry (index) {
    const label = this.getItemLabel(this.value[index], index)

    const tabText = document.createElement('span')
    tabText.textContent = label

    const tab = this.theme.getTab(tabText, this.getValidId(`${this.path}.${index}`))
    this.theme.addTab(this.tabs_holder, tab)
    tab.setAttribute('data-i', index)

    tab.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      // Dynamically resolve current index so it stays correct after deletes
      const idx = this.rows.findIndex(r => r && r.tab === tab)
      if (idx >= 0) this.selectChild(idx)
    })

    this._addTabDragHandlers(tab)

    this.rows[index] = { tab, tab_text: tabText }
  }

  /** Update the active-state class on all nav pills. */
  _refreshTabNav () {
    this.rows.forEach((row, i) => {
      if (!row || !row.tab) return
      const link = row.tab.querySelector('a') || row.tab.firstChild
      if (link) {
        link.classList.toggle('active', i === this.currentIndex)
      }
    })
  }

  // ─── TABS DRAG AND DROP ────────────────────────────────────────────────────

  _addTabDragHandlers (tab) {
    tab.setAttribute('draggable', 'true')
    tab.style.cursor = 'grab'

    const [draggable] = Draggable.create(tab, {
      type: 'y',
      onDragStart: () => {
        this._dragSrcIndex = this.rows.findIndex(r => r && r.tab === tab)
        tab.classList.add('sortable-ghost')
        gsap.to(tab, { opacity: 1, scale: 1.05, duration: 0.15 })
        this._showDropIndicator()
        // Measure container and nav once per drag — no further getBoundingClientRect in drag loop
        const holderRect = this.tabs_holder.getBoundingClientRect()
        const tabRect = tab.getBoundingClientRect()
        draggable.applyBounds({
          minY: holderRect.top - tabRect.top,
          maxY: holderRect.bottom - tabRect.bottom
        })
        const nav = this.tabs_holder.children[0].children[0]
        this._dragRafPending = false
      },
      onDragEnd: () => {
        gsap.set(tab, { clearProps: 'x,y,transform' })
        gsap.to(tab, { opacity: 1, scale: 1, duration: 0.15 })
        tab.classList.remove('sortable-ghost')
        this._hideDropIndicator()

        const srcIdx = this._dragSrcIndex
        const targetTab = this._dropTargetTab
        this._dragSrcIndex = null
        this._dropTargetTab = null

        if (srcIdx === null || srcIdx === undefined || !targetTab) return

        const targetIdx = this.rows.findIndex(r => r && r.tab === targetTab)
        if (targetIdx < 0 || srcIdx === targetIdx) return

        // Flush current editor value before mutating
        if (this.ready && this.currentIndex >= 0 && this.value[this.currentIndex] !== undefined) {
          this.value[this.currentIndex] = this.child.getValue()
        }

        // Reorder value array
        let insertAt = this._dropBefore ? targetIdx : targetIdx + 1
        const moved = this.value.splice(srcIdx, 1)[0]
        if (insertAt > srcIdx) insertAt--
        this.value.splice(insertAt, 0, moved)
        this.currentIndex = insertAt

        // Rebuild tab nav
        this._clearTabEntries()
        this.serialized = null
        for (let i = 0; i < this.value.length; i++) {
          this._addTabEntry(i)
        }

        // this._animateTabsIn()
        this.updateChildValue(this.currentIndex)
        this._refreshTabNav()
        this._refreshMoveButtons()
        this._refreshAddDeleteButtons()
        this.serialized = JSON.stringify(this.value)
        this.onChange(true)
      },
      onDrag: (e) => {
        // Throttle to one update per animation frame to avoid excessive reflows
        if (this._dragRafPending) return
        this._dragRafPending = true
        const clientX = e.clientX
        const clientY = e.clientY
        requestAnimationFrame(() => {
          this._dragRafPending = false
          // Temporarily disable pointer events on the dragged tab so we can
          // hit-test what it is currently overlapping in the nav list.
          // Batch: one write, one read, one write — then no more reads before next write.
          tab.style.pointerEvents = 'none'
          const elements = document.elementsFromPoint(clientX, clientY)
          tab.style.pointerEvents = ''

          const targetTab = elements.find(el => this.rows.some(r => r && r.tab === el))
          if (targetTab) {
            // Read tabRect only to determine above/below midpoint; _positionDropIndicator uses offsetTop instead
            const tabRect = targetTab.getBoundingClientRect()
            const before = clientY < tabRect.top + tabRect.height / 2
            this._positionDropIndicator(targetTab, before)
            this._dropBefore = before
            this._dropTargetTab = targetTab
          }
        })
      }
    })

  }

  /** Create (once) and fade-in the glowing drop-indicator line inside the nav ul. */
  // Below is the visual indicator of where the dragged tab will be dropped. It's a blue line that appears between tabs as you drag, showing the potential drop location. The line has a subtle glow effect to make it stand out against the background, and it smoothly fades in when you start dragging a tab. The indicator moves up and down in real-time as you hover over different tabs, providing clear visual feedback on where the tab will be inserted if you release the mouse button.
  _showDropIndicator () {
    const nav = this.tabs_holder.children[0].children[0]
    nav.style.position = 'relative'

    if (!this._dropIndicatorEl || !this._dropIndicatorEl.parentNode) {
      const el = document.createElement('div')
      Object.assign(el.style, {
        width: '100%',
        position: 'absolute',
        left: '0',
        height: '3px',
        backgroundColor: 'var(--color-grey-700)',
        boxShadow: '0 0 5px 3px var(--color-grey-500)',
        pointerEvents: 'none',
        zIndex: '1',
        opacity: '0',
        top: '0'
      })
      nav.appendChild(el)
      this._dropIndicatorEl = el
    }

    gsap.to(this._dropIndicatorEl, { opacity: 1, duration: 0.1 })
  }

  /** Fade-out the drop indicator. */
  _hideDropIndicator () {
    if (this._dropIndicatorEl) {
      gsap.to(this._dropIndicatorEl, { opacity: 0, duration: 0.15 })
    }
  }

  /** Slide the indicator to sit above or below the hovered tab. */
  _positionDropIndicator (tab, before) {
    if (!this._dropIndicatorEl) return
    const y = (before ? tab.offsetTop : tab.offsetTop + tab.offsetHeight) - 2
    gsap.to(this._dropIndicatorEl, { top: y, duration: 0.1, ease: 'power2.out' })
  }

  /** Stagger-animate all tab pills back in after a reorder. */
  _animateTabsIn () {
    this.rows.forEach((row, i) => {
      if (!row || !row.tab) return
      gsap.fromTo(
        row.tab,
        { x: -14, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.1, delay: i * 0.04, ease: 'power2.out' }
      )
    })
  }

  /** Show/hide move buttons based on the current item's position. */
  _refreshMoveButtons () {
    if (!this.moveUpButton || !this.moveDownButton) return
    const idx = this.currentIndex
    const last = this.value.length - 1
    if (idx === undefined || idx <= 0 || this?.options?.disable_array_reorder) {
      this.moveUpButton.style.setProperty('display', 'none', 'important')
    } else {
      this.moveUpButton.style.removeProperty('display')
    }
    if (idx === undefined || idx >= last || this?.options?.disable_array_reorder) {
      this.moveDownButton.style.setProperty('display', 'none', 'important')
    } else {
      this.moveDownButton.style.removeProperty('display')
    }

  }
  _refreshAddDeleteButtons () {
    if (this.isBasic) {
      this.copyButton.style.setProperty('display', 'none', 'important')
    }
    if ((this.isBasic && this.options.disable_array_add) || this.value.length >= this.getMax()) {
      this.add_row_button?.style.setProperty('display', 'none', 'important')
    }
    if (this.isBasic && this.options.disable_array_delete) {
      this.deleteButton.style.setProperty('display', 'none', 'important')
    }
  }

  /** Sync the nav pill labels to current value labels. */
  _refreshTabLabels () {
    this.rows.forEach((row, i) => {
      if (row && row.tab_text && this.value[i] !== undefined) {
        row.tab_text.textContent = this.getItemLabel(this.value[i], i)
      }
    })
    this.child.header.innerHTML = `<span>${this.getItemLabel(this.value[this.currentIndex], this.currentIndex)}</span>`
  }

  _tabsRefreshValue () {
    if (this.ready && this.currentIndex >= 0 && this.value[this.currentIndex] !== undefined) {
      this.value[this.currentIndex] = this.child.getValue()
    }
    this.serialized = JSON.stringify(this.value)
  }

  /** Add-row button for tabs mode */
  _tabsCreateAddButton () {
    if (this.schema.maxItems >= 0 && this.value?.length >= this.getMax()) return
    const button = this.getButton(this.getItemTitle(), 'add', this.translate('button_add_row_title', [this.getItemTitle()]))
    button.classList.add('json-editor-btntype-add')
    button.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.addChild()
    })
    this.controls.appendChild(button)
    return button
  }

  /** Move the currently active item one position down (higher index). */
  moveDown (index) {
    index = _.isNumber(index) ? index : this.currentIndex
    if (index === undefined || index >= this.value.length - 1) return

    // Flush current editor value before mutating
    if (this.ready && this.currentIndex >= 0 && this.value[this.currentIndex] !== undefined) {
      this.value[this.currentIndex] = this.child.getValue()
    }

    const tmp = this.value[index]
    this.value[index] = this.value[index + 1]
    this.value[index + 1] = tmp
    this.currentIndex = index + 1

    this._rebuildAfterMove()
  }

  /** Move the currently active item one position up (lower index). */
  moveUp (index) {
    index = _.isNumber(index) ? index : this.currentIndex
    if (index === undefined || index <= 0) return

    // Flush current editor value before mutating
    if (this.ready && this.currentIndex >= 0 && this.value[this.currentIndex] !== undefined) {
      this.value[this.currentIndex] = this.child.getValue()
    }

    const tmp = this.value[index]
    this.value[index] = this.value[index - 1]
    this.value[index - 1] = tmp
    this.currentIndex = index - 1

    this._rebuildAfterMove()
  }

  /** Shared post-move rebuild used by both moveUp and moveDown. */
  _rebuildAfterMove () {
    if (this.useTabs) {
      this._clearTabEntries()
      this.serialized = null
      for (let i = 0; i < this.value.length; i++) {
        this._addTabEntry(i)
      }
      this.updateChildValue(this.currentIndex)
      this._refreshTabNav()
      this.serialized = JSON.stringify(this.value)
    } else {
      this.serialized = null // force setValue to re-render
      this.setValue(this.value)
    }
    this._refreshMoveButtons()
    this.onChange(true)
  }


  /** Remove all nav pill entries from the DOM (not the child editor). */
  _clearTabEntries () {
    this.rows.forEach(row => {
      if (row && row.tab && row.tab.parentNode) row.tab.parentNode.removeChild(row.tab)
    })
    this.rows = []
    // Keep the drop indicator in the nav so it can be reused; it will be re-appended
    // by _showDropIndicator if it has been detached.
    if (this._dropIndicatorEl && !this._dropIndicatorEl.parentNode) {
      this._dropIndicatorEl = null
    }
  }

  /** DFS search for a hotspot child data attribute. */
  _findHotspotChild (element, maxDepth = 5) {
    const queue = [{ node: element, depth: 0 }]
    while (queue.length) {
      const { node, depth } = queue.shift()
      if (depth > maxDepth) continue
      if (node.tagName === 'DIV' && node.dataset?.hotspotInstance) {
        return node.dataset.hotspotInstance
      }
      for (const child of node.children) {
        if (child.tagName === 'DIV') queue.push({ node: child, depth: depth + 1 })
      }
    }
    return null
  }

  // ─── SHARED ─────────────────────────────────────────────────────────────────

  getDefault () {
    return this.schema.default || []
  }

  getMax () {
    return ArrayEditor.prototype.getMax.apply(this, arguments)
  }

  setValue (value = [], initial) {
    if (this.useTabs) {
      return this._tabsSetValue(value, initial)
    }
    return this._defaultSetValue(value, initial)
  }

  /** Tabs mode setValue: rebuild nav labels, then show active item in the single pane. */
  _tabsSetValue (value = [], initial) {
    value = ArrayEditor.prototype.ensureArraySize.call(this, value)

    const serialized = JSON.stringify(value)
    if (serialized === this.serialized) return

    this.value = value

    // Remove surplus nav entries
    while (this.rows.length > value.length) {
      const row = this.rows.pop()
      if (row && row.tab && row.tab.parentNode) row.tab.parentNode.removeChild(row.tab)
    }

    // Add missing nav entries (labels only – no editor built yet)
    for (let i = this.rows.length; i < value.length; i++) {
      this._addTabEntry(i)
    }


    // Select first tab if nothing is active yet
    if (this.currentIndex === undefined || this.currentIndex < 0) {
      this.currentIndex = value.length > 0 ? 0 : undefined
    }
    // Clamp if items were removed
    if (this.currentIndex !== undefined && this.currentIndex >= value.length) {
      this.currentIndex = value.length - 1
    }

    if (this.currentIndex !== undefined && this.currentIndex >= 0) {
      this.updateChildValue(this.currentIndex)
    } else {
      this.holder.style.display = 'none'
    }

    this._refreshTabNav()
    this.serialized = JSON.stringify(this.value)
    this.onChange(true)
    this._refreshMoveButtons()
    this._refreshAddDeleteButtons()
    this._refreshTabLabels()

  }

  /** Non-tabs mode setValue (original behaviour). */
  _defaultSetValue (value = [], initial) {
    value = ArrayEditor.prototype.ensureArraySize.call(this, value)

    const serialized = JSON.stringify(value)
    if (serialized === this.serialized) return

    this.value = value

    if (this.value && this.value.length) {
      this.currentIndex = _.clamp(
        _.isNumber(this.currentIndex) ? this.currentIndex : 0,
        0,
        this.value.length - 1
      )
      this.updateChildValue(this.currentIndex)
    } else {
      this.currentIndex = undefined
      this.child.setValue({}, false)
      this.holder.style.display = 'none'
      this.updateChoices()
    }

    this.onChange(true)
  }

  // ─── NON-TABS MODE HELPERS ──────────────────────────────────────────────────

  updateChoices () {
    if (this.value && this.value.length) {
      if (!_.isNumber(this.currentIndex) || this.currentIndex < 0 || this.currentIndex >= this.value.length) {
        this.currentIndex = 0
      }

      // Compute labels before sorting so index-based fallback labels are stable.
      const currentItem = this.value[this.currentIndex]
      const labelled = this.value.map((item, i) => ({ item, index: i, label: this.getItemLabel(item, i) }))
      this._labelCollator = this._labelCollator || new Intl.Collator(undefined, {
        sensitivity: 'base',
        numeric: true
      })
      labelled.sort((a, b) => this._labelCollator.compare(a.label, b.label))

      // Keep data order as-is; only sort the dropdown presentation.
      this.currentIndex = this.value.indexOf(currentItem)
      if (this.currentIndex < 0) this.currentIndex = 0

      this.serialized = JSON.stringify(this.value)

      const choices = labelled.map((l) => ({
        value: l.index,
        label: l.label,
        selected: l.index === this.currentIndex,
        disabled: false
      }))

      this.copyButton.style.display = ''
      this.deleteButton.style.display = ''
      this.holder.style.display = ''

      this.choices.setChoices(choices, 'value', 'label', true)
      this.choices.setChoiceByValue(String(this.currentIndex))
      this.choices.enable()
    } else {
      this.choices.setChoices([{
        value: '',
        label: 'No items available',
        selected: true,
        disabled: true
      }], 'value', 'label', true)
      this.choices.disable()

      this.copyButton.style.display = 'none'
      this.deleteButton.style.display = 'none'
      this.holder.style.display = 'none'
    }

    // this._refreshAddDeleteButtons()
  }

  selectChild (index) {
    if (index !== undefined) {
      // Commit any pending debounced input before switching active index.
      this.onChildEditorChange.flush()
      // Save the currently displayed value before switching tabs
      if (this.ready && this.currentIndex !== undefined && this.currentIndex >= 0 &&
          this.value[this.currentIndex] !== undefined) {
        this.value[this.currentIndex] = this.child.getValue()
      }
      this.currentIndex = index
    }
    this.updateChildValue(this.currentIndex)
    if (this.useTabs) {
      this._refreshTabNav()
      // Notify any hotspot child of the new active tab
      const hotspotChild = this._findHotspotChild(this.holder)
        || this.holder.dataset?.hotspotInstance
      if (hotspotChild) {
        EventBus.$emit(`hotspot:newActiveTab${hotspotChild}`, hotspotChild)
      }
    }
    this._refreshMoveButtons()
  }

  updateChildValue (index, value = undefined) {
    if (value === undefined) {
      value = this.value[index] || {}
    }

    if (value) {
      this.child.path = `${this.path}.child.${index}`
      this.child.key = index
      this.child.setValue(value, false)
      if (this.child.header) {
        this.child.header.innerHTML = `<span>${this.getItemLabel(value, index)}</span>`
      }
      this.holder.style.display = ''
    } else {
      this.child.setValue({}, false)
      this.holder.style.display = 'none'
    }
    if(!this.useTabs)
    {
      this.updateChoices()
    }
  }

  deleteChild (index) {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return
    }

    index = _.isNumber(index) ? index : this.currentIndex

    // Save current editor value before mutating
    if (this.ready && this.currentIndex >= 0 && this.value[this.currentIndex] !== undefined) {
      this.value[this.currentIndex] = this.child.getValue()
    }

    const newval = this.getValue().filter((row, j) => j !== index)
    const newIndex = _.clamp(index, 0, Math.max(0, newval.length - 1))
    this.currentIndex = newval.length > 0 ? newIndex : undefined

    const editor = this.child

    if (this.useTabs) {
      // Rebuild nav entries after deletion so indices are correct
      this._clearTabEntries()
      this.value = newval
      this.serialized = null // force re-render
      for (let i = 0; i < newval.length; i++) {
        this._addTabEntry(i)
      }
      if (this.currentIndex !== undefined && this.currentIndex >= 0) {
        this.updateChildValue(this.currentIndex)
        this._refreshTabNav()
      } else {
        this.holder.style.display = 'none'
      }
      this.serialized = JSON.stringify(this.value)
    } else {
      this.setValue(newval)
    }

    this.onChange(true)
    this.jsoneditor.trigger('deleteRow', editor)
  }

  addChild (child) {
    if (this.value.length >= this.getMax()) return
    child = child !== undefined ? child : _.cloneDeep(this.schema.items.default || {})

    // Commit pending debounced edits and save current editor value before pushing.
    this.onChildEditorChange.flush()
    if (this.ready && this.currentIndex >= 0 &&
        this.value[this.currentIndex] !== undefined) {
      this.value[this.currentIndex] = this.child.getValue()
    }

    const newval = Array.isArray(this.value) ? this.value.slice() : []
    newval.push(child)
    const newIndex = newval.length - 1
    this.currentIndex = newIndex

    const editor = this.child

    if (this.useTabs) {
      this.value = newval
      this._addTabEntry(newIndex)
      this.updateChildValue(newIndex)
      this._refreshTabNav()
      this.serialized = JSON.stringify(this.value)
    } else {
      this.value = newval
      this.serialized = null
      this.updateChildValue(newIndex, child)
    }

    this.onChange(true)
    this.jsoneditor.trigger('addRow', editor)
  }

  duplicateChild (index) {
    index = _.isNumber(index) ? index : this.currentIndex
    const copy = _.cloneDeep(this.value[index])
    this.addChild(copy)
  }

  // ─── SHARED HELPERS ─────────────────────────────────────────────────────────

  getItemId (option, index) {
    const title = this.schema.items.title || 'item'
    const compiled = this.itemTitleTmpl({ self: option, i: index, i1: index + 1 })
    return compiled || _.kebabCase(title + ' ' + (index + 1))
  }

  getItemLabel (option, index) {
    const title = this.schema.items.title || 'item'
    const compiled = this.itemTitleTmpl({ self: option, i: index, i1: index + 1 })
    return compiled || _.startCase(title + ' ' + (index + 1))
  }

  getItemTitle () {
    return ArrayEditor.prototype.getItemTitle.apply(this, arguments)
  }

  getItemSchema (i) {
    return ArrayEditor.prototype.getItemSchema.apply(this, arguments)
  }

  getItemInfo (i) {
    return ArrayEditor.prototype.getItemInfo.apply(this, arguments)
  }
}

export default LargeArrayEditor
